import React, { forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './ReviewShareCard.module.sass';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import { displayHandle } from '../../utils/streamHelpers';

/**
 * ReviewShareCard — the branded, social-ready review graphic (frosted glass
 * over photo). Renders as real DOM at a fixed internal 1080x1920 (9:16,
 * phone/story portrait) canvas, then scales to fit its container with a CSS
 * transform, so the SAME node previews at any size and rasterizes pixel-
 * perfect at full resolution.
 *
 * Purely presentational: no fetching, no upload. Accepts the data shape both
 * the Collab Tracker and the public ReviewsFeed can provide.
 *
 * The wordmark is the white transparent Postaway lockup (stamp "p" +
 * wordmark) at public/images/postaway-logo-white.png.
 */

export const CARD_WIDTH = 1080;
export const CARD_HEIGHT = 1920;

export const WORDMARK_LOGO_SRC = '/images/postaway-logo-white.png';

const MAX_TEXT_CHARS = 400;
const SHORT_TEXT_CHARS = 120;

// Same star geometry as the ReviewsFeed StarRating component.
const STAR_PATH = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';

/**
 * Two independent images on the card:
 *   background (large, blurred) = backgroundImageUrl || reviewerAvatarUrl
 *   circle (small portrait)     = circleImageUrl     || subjectAvatarUrl
 *
 * i.e. by default the card shows the SUBJECT (who the review is about) in the
 * circle and the AUTHOR (who wrote it) as the background. Either can be
 * overridden with a custom upload.
 *
 * Exported so ReviewShareModal can preload the exact same assets before
 * enabling the download button.
 */
// Local object URLs (live preview while uploading) and data URIs must not be
// routed through Cloudinary fetch delivery.
const isLocalUrl = (url) =>
  typeof url === 'string' && (url.startsWith('blob:') || url.startsWith('data:'));

const toSrc = (url, opts) => {
  if (!url) return null;
  return isLocalUrl(url) ? url : (getCloudinaryUrl(url, opts) || url);
};

export function resolveCardImages({ backgroundImageUrl, circleImageUrl, subjectAvatarUrl, reviewerAvatarUrl }) {
  // Preferred: background = author, circle = subject. When a party has no
  // photo, fall through to whatever real image is available so the circle
  // never collapses to the blank "P" placeholder (and the background never
  // collapses to the navy gradient when a photo exists).
  const bgSource = backgroundImageUrl || reviewerAvatarUrl || subjectAvatarUrl || null;
  const circleSource = circleImageUrl || subjectAvatarUrl || reviewerAvatarUrl || backgroundImageUrl || null;
  const bgSrc = toSrc(bgSource, { width: CARD_WIDTH, height: CARD_HEIGHT, crop: 'fill', quality: 'auto', format: 'auto' });
  const circleSrc = toSrc(circleSource, { width: 360, height: 360, crop: 'fill', quality: 'auto', format: 'auto' });
  return { bgSrc, circleSrc };
}

const StarRow = ({ rating }) => {
  const r = Math.max(0, Math.min(5, Math.round(rating || 0)));
  return (
    <div className={styles.stars}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          width="52"
          height="52"
          viewBox="0 0 24 24"
          fill={i <= r ? '#f5a623' : 'none'}
          stroke={i <= r ? 'none' : 'rgba(255, 255, 255, 0.65)'}
          strokeWidth={i <= r ? 0 : 1.4}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={STAR_PATH} />
        </svg>
      ))}
    </div>
  );
};

const Wordmark = () => (
  <div className={styles.wordmark}>
    <img
      src={WORDMARK_LOGO_SRC}
      alt="Postaway"
      crossOrigin="anonymous"
      className={styles.wordmarkLogo}
    />
  </div>
);

/** Shrink a handle line's font until the (estimated) text fits one row. */
function handleFontSize(line, base) {
  const MIN = 26;
  // Available width inside the outer card ≈ 1080 - 2*60 (inset) - 2*70 (padding).
  const available = CARD_WIDTH - 120 - 140;
  // Rough average glyph width for Poppins ≈ 0.58em.
  const estimated = line.length * base * 0.58;
  if (estimated <= available) return base;
  return Math.max(MIN, Math.floor(base * (available / estimated)));
}

const ReviewShareCard = forwardRef(function ReviewShareCard(
  {
    backgroundImageUrl = null,
    circleImageUrl = null,
    subjectAvatarUrl = null,
    reviewerAvatarUrl = null,
    reviewText = '',
    rating = 5,
    subjectName = '',
    subjectHandle = null,
    reviewerHandle = null,
    reviewType = 'creator',
    scaleToFit = true,
    className,
  },
  ref
) {
  const scalerRef = useRef(null);
  const [scale, setScale] = useState(scaleToFit ? 0 : 1);
  const [heroFailed, setHeroFailed] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    setHeroFailed(false);
    setPhotoFailed(false);
  }, [backgroundImageUrl, circleImageUrl, subjectAvatarUrl, reviewerAvatarUrl]);

  useEffect(() => {
    if (!scaleToFit) return undefined;
    const el = scalerRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / CARD_WIDTH);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scaleToFit]);

  const { bgSrc, circleSrc } = resolveCardImages({ backgroundImageUrl, circleImageUrl, subjectAvatarUrl, reviewerAvatarUrl });
  const showHero = !!bgSrc && !heroFailed;
  const showPhoto = !!circleSrc && !photoFailed;

  // Stacked handles: the subject (who the review is about) leads, the
  // reviewer follows after the "x"; the "by:" line under the panel makes
  // authorship explicit.
  const subjectTag = displayHandle(subjectHandle) || subjectName;
  const reviewerTag = displayHandle(reviewerHandle);

  // Review text: truncate past ~400 chars; bump size for short quotes.
  const text = String(reviewText || '').trim();
  const truncated = text.length > MAX_TEXT_CHARS ? `${text.slice(0, MAX_TEXT_CHARS).trimEnd()}...` : text;
  const textFontSize = truncated.length < SHORT_TEXT_CHARS ? 50 : 40;

  const canvas = (
    <div
      ref={ref}
      className={styles.canvas}
      style={scaleToFit ? { transform: `scale(${scale})` } : undefined}
    >
      {/* Layer 1: full-bleed photo (blurred + darkened) or navy gradient */}
      <div className={styles.bg}>
        {showHero ? (
          <img
            src={bgSrc}
            alt=""
            crossOrigin="anonymous"
            className={styles.bgImg}
            onError={() => setHeroFailed(true)}
          />
        ) : (
          <div className={styles.bgFallback} />
        )}
        <div className={styles.bgOverlay} />
      </div>

      {/* Layer 2: frosted outer card */}
      <div className={styles.outerCard}>
        <Wordmark />

        {/* Card title */}
        <div className={styles.cardTitle}>Review</div>

        {/* Layer 3: stacked handles, between the title and the circle photo
            (tighter gap above, looser below, via the flex spacers) */}
        <div className={styles.flexSpacerSmall} />
        {subjectTag && (
          <div className={styles.handleStack}>
            <div
              className={styles.handlePrimary}
              style={{ fontSize: `${handleFontSize(subjectTag, 42)}px` }}
            >
              {subjectTag}
            </div>
            {reviewerTag && (
              <>
                <div className={styles.handleX}>x</div>
                <div
                  className={styles.handlePrimary}
                  style={{ fontSize: `${handleFontSize(reviewerTag, 42)}px` }}
                >
                  {reviewerTag}
                </div>
              </>
            )}
          </div>
        )}
        <div className={styles.flexSpacer} />

        {/* Layer 4: inner frosted panel + author signature */}
        <div className={styles.panelBlock}>
          <div className={styles.innerPanel}>
            <div className={styles.photoCircle}>
              {showPhoto ? (
                <img
                  src={circleSrc}
                  alt=""
                  crossOrigin="anonymous"
                  className={styles.photoImg}
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                <span className={styles.photoPlaceholder}>p</span>
              )}
            </div>

            {truncated && (
              <p className={styles.reviewText} style={{ fontSize: `${textFontSize}px` }}>
                &ldquo;{truncated}&rdquo;
              </p>
            )}

            <StarRow rating={rating} />
          </div>

          {reviewerTag && <div className={styles.byline}>by: {reviewerTag}</div>}
        </div>
        <div className={styles.flexSpacer} />

        {/* Layer 5: brand handle */}
        <div className={styles.brandHandle}>@postawaytravels</div>
      </div>
    </div>
  );

  if (!scaleToFit) {
    return <div className={cn(styles.fixed, className)}>{canvas}</div>;
  }

  return (
    <div
      ref={scalerRef}
      className={cn(styles.scaler, className)}
      style={{ height: scale > 0 ? `${CARD_HEIGHT * scale}px` : undefined }}
    >
      {scale > 0 && canvas}
    </div>
  );
});

export default ReviewShareCard;
